/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { task } from 'ember-concurrency';
import { acceptedFileTypes, cropImage, getImageFromDataUrl } from 'cio/utils/images';
import { blobToDataUrl, getUploadFn, SignResponse } from 'cio/utils/uploads';
import AccountModel from 'cio/models/account';
import ApiErrorsService from 'cio/services/api-errors';

const MAX_IMAGE_SIZE = 500;
const MIN_IMAGE_SIZE = 200;

interface LogoUploaderArgs {
  account: AccountModel;
  isAccountLogoDirty: boolean;
  tempAccountLogoUrl: string;
  onDeleted: () => unknown;
  onUploaded: (url: string) => unknown;
}

export default class LogoUploader extends Component<LogoUploaderArgs> {
  @service apiErrors!: ApiErrorsService;

  readonly acceptedFileTypes = acceptedFileTypes;

  @tracked errors: { message: string }[] = [];

  validateImage(image: { height: number; width: number }) {
    let errors = [];

    if ([image.width, image.height].some((dim) => dim < MIN_IMAGE_SIZE)) {
      errors.push({ message: 'Logos must be at least 200 x 200 pixels.' });
    }

    return errors;
  }

  signFile = task(async (filename: string, filetype: string, filesize: number) => {
    return await this.args.account.signLogo({
      name: encodeURI(filename),
      type: encodeURI(filetype),
      size: String(filesize),
    });
  });

  uploadFile = task(async (file: File, json: SignResponse) => {
    let { uploadFn, data } = getUploadFn(json);
    let { key } = await uploadFn(file, data, {});
    return key;
  });

  uploadLogo = task(async (file: File) => {
    this.errors = [];

    try {
      let blobDataUrl = await blobToDataUrl(file);
      let image = await getImageFromDataUrl(blobDataUrl);

      let validationErrors = this.validateImage(image);
      if (isPresent(validationErrors)) {
        this.errors = validationErrors;
        return;
      }

      let croppedFile = file;

      if (image.width !== image.height) {
        croppedFile = await cropImage(image, MAX_IMAGE_SIZE);
      }

      let json = await this.signFile.perform(file.name, file.type, file.size);
      let logoUrl = await this.uploadFile.perform(croppedFile, json);

      this.args.onUploaded(logoUrl);
    } catch (err) {
      this.apiErrors.report({ errors: [err] }, { externalAPIError: true });
      throw err;
    }
  });
}
